import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { api } from '../../../../services/api';
import { DetailSection } from '../../TripDetailPanel.styles';

interface Driver {
  id: number;
  name: string;
  mobileNumber?: string;
  licenseNumber?: string;
  rating?: number;
}

interface DriverData {
  id: number;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  contactNumber?: string;
  licenseNumber?: string;
  rating?: number;
  [key: string]: any;
}

// Define possible API response structures
interface DriverResponse {
  data: DriverData[] | {
    items?: DriverData[];
    drivers?: DriverData[];
    content?: DriverData[];
    results?: DriverData[];
    data?: DriverData[];
    [key: string]: any;
  } | any;
}

interface TripDriverSectionProps {
  loading: boolean;
  tripCode: string;
  currentDriverId?: number | null;
  isInProgress?: boolean;
  driverData?: {
    id: number;
    firstName?: string;
    lastName?: string;
    mobileNumber?: string;
    email?: string;
    role?: string;
    licenseNumber?: string;
    rating?: number;
  } | null;
  onUpdate: () => Promise<void>;
  customerName?: string;
  customerMobile?: string;
  pickupTime?: string;
  carType?: string;
  carNumber?: string;
  disabled?: boolean;
  currentStatus?: string;
  customer?: {
    id?: number;
    name?: string;
    email?: string;
    mobileNumber?: string;
    countryCode?: string;
    address?: string;
  } | null;
}

const TripDriverSection: React.FC<TripDriverSectionProps> = ({
  loading,
  tripCode,
  currentDriverId,
  driverData,
  pickupTime,
  carType,
  carNumber,
  onUpdate,
  customerName,
  customerMobile,
  disabled = false,
  currentStatus,
  customer,
  isInProgress
}) => {
  // Always show driver details by default if a driver is assigned
  const [showDriverDetails] = useState<boolean>(true);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(currentDriverId || null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // WhatsApp notification states
  const [openWhatsAppDialog, setOpenWhatsAppDialog] = useState<boolean>(false);
  const [sendingNotification, setSendingNotification] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Add ref for scroll position preservation
  const driverSectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);
  
  // Initialize assignedDriver with driverData if available
  const [assignedDriver, setAssignedDriver] = useState<Driver | null>(
    driverData ? {
      id: driverData.id,
      name: `${driverData.firstName || ''} ${driverData.lastName || ''}`.trim(),
      mobileNumber: driverData.mobileNumber || '',
      licenseNumber: driverData.licenseNumber || '',
      rating: driverData.rating || 0
    } : null
  );

  // Preserve scroll position after operations complete
  useEffect(() => {
    if (shouldPreserveScroll && driverSectionRef.current) {
      // Small delay to ensure DOM updates are complete
      setTimeout(() => {
        driverSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setShouldPreserveScroll(false);
      }, 100);
    }
  }, [drivers, selectedDriverId, success, error, snackbarOpen, shouldPreserveScroll]);

  // Define fetchDrivers function using useCallback to memoize it
  const fetchDrivers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.getAllDrivers();
      
      if (response && response.data) {
        let driversArray: any[] = [];
        
        // Check different possible response formats
        if (Array.isArray(response.data)) {
          driversArray = response.data;
        } else if (typeof response.data === 'object') {
          // Check for common container properties in REST APIs
          if (Array.isArray((response.data as any).items)) {
            driversArray = (response.data as any).items;
          } else if (Array.isArray((response.data as any).drivers)) {
            driversArray = (response.data as any).drivers;
          } else if (Array.isArray((response.data as any).content)) {
            driversArray = (response.data as any).content;
          } else if (Array.isArray((response.data as any).results)) {
            driversArray = (response.data as any).results;
          } else if (Array.isArray((response.data as any).data)) {
            driversArray = (response.data as any).data;
          }
        }
        
        // Map the driver data to a consistent format
        const mappedDrivers = driversArray.map((driver: any) => ({
          id: driver.id,
          name: `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || driver.name || 'Unknown',
          mobileNumber: driver.mobileNumber || driver.contactNumber || '',
          licenseNumber: driver.licenseNumber || '',
          rating: driver.rating || 0
        }));
        
        // If we have an assigned driver from trip details, add it to the list if not already present
        if (assignedDriver && !mappedDrivers.some(driver => driver.id === assignedDriver.id)) {
          // Ensure the driver has all required properties for the mappedDrivers array
          const driverToAdd = {
            id: assignedDriver.id,
            name: assignedDriver.name,
            mobileNumber: assignedDriver.mobileNumber || '',
            licenseNumber: assignedDriver.licenseNumber || '',
            rating: assignedDriver.rating || 0
          };
          mappedDrivers.unshift(driverToAdd);
        }
        
        setDrivers(mappedDrivers);
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setError('Failed to fetch drivers. Please try again.');
      setDrivers([]);
    } finally {
      setIsLoading(false);
    }
  }, [assignedDriver]);
  
  // Fetch all drivers when component mounts
  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);
  
  // Update selected driver when currentDriverId or driverData prop changes
  useEffect(() => {
    if (currentDriverId) {
      setSelectedDriverId(currentDriverId);
    }
  }, [currentDriverId]);
  
  // Update assignedDriver when driverData changes
  useEffect(() => {
    if (driverData) {
      setAssignedDriver({
        id: driverData.id,
        name: `${driverData.firstName || ''} ${driverData.lastName || ''}`.trim(),
        mobileNumber: driverData.mobileNumber || '',
        licenseNumber: driverData.licenseNumber || '',
        rating: driverData.rating || 0
      });
    }
  }, [driverData]);
  
  // Fetch trip details to get assigned driver info if needed
  useEffect(() => {
    // If we have a currentDriverId but no assigned driver info, fetch it
    const fetchAssignedDriverIfNeeded = async () => {
      if (currentDriverId && tripCode && !assignedDriver) {
        try {
          const tripDetails = await api.getTripDetails(tripCode) as any;
          if (tripDetails && tripDetails.driver && typeof tripDetails.driver === 'object') {
            const driver = tripDetails.driver;
            setAssignedDriver({
              id: driver.id,
              name: `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || 'Unknown',
              mobileNumber: driver.mobileNumber || driver.contactNumber || '',
              licenseNumber: driver.licenseNumber || '',
              rating: driver.rating || 0
            });
          }
        } catch (err) {
          console.error('Error fetching assigned driver details:', err);
        }
      }
    };
    
    fetchAssignedDriverIfNeeded();
  }, [currentDriverId, tripCode, assignedDriver]);
  
  const handleDriverChange = (event: SelectChangeEvent<number>) => {
    const driverId = event.target.value as number;
    setSelectedDriverId(driverId);
  };
  
  const handleAssignDriver = async () => {

    if (currentStatus !== 'CONFIRMED') {
    setError('Please confirm the trip before assigning a driver.');
    return;
    }
    if (!selectedDriverId) {
      setError('Please select a driver first');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      setShouldPreserveScroll(true);
      
      // Call the assign driver API
      await api.assignDriverToTrip(tripCode, selectedDriverId);
      await api.updateTripStatus(tripCode, 'DRIVER ASSIGNED');  
      setSuccess('Driver successfully assigned to the trip');
      
      // Trigger parent update to refresh trip details
      await onUpdate();
    } catch (err: any) {
      console.error('Error assigning driver:', err);
      setError('Failed to assign driver. ' + (err.message || ''));
      setShouldPreserveScroll(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRemoveDriver = async () => {
    if (!currentDriverId) {
      setError('No driver assigned to remove');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      setShouldPreserveScroll(true);
      
      await api.removeDriverFromTrip(tripCode);

      if(currentStatus === 'DRIVER ASSIGNED'){
        await  api.updateTripStatus(tripCode, 'CONFIRMED');
      }
      
      setSuccess('Driver successfully removed from the trip');
      setSelectedDriverId(null);
      setAssignedDriver(null);
      
      await onUpdate();
    } catch (err: any) {
      console.error('Error removing driver:', err);
      setError('Failed to remove driver. ' + (err.message || ''));
      setShouldPreserveScroll(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  // WhatsApp notification handlers
  const handleOpenWhatsAppDialog = () => {
    setOpenWhatsAppDialog(true);
  };

  const handleCloseWhatsAppDialog = () => {
    setOpenWhatsAppDialog(false);
  };
  
  const handleSendWhatsAppNotification = async () => {
    if (!assignedDriver || !customerMobile) {
      setSnackbarMessage('Missing driver or customer information');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(true); // Set flag to preserve scroll
      return;
    }
    
    setSendingNotification(true);
    setShouldPreserveScroll(true); // Set flag to preserve scroll
    try {
       let mobileNumber = customerMobile.replace(/\D/g, '');
       let countryCode = customer?.countryCode || '91';
        mobileNumber = mobileNumber.startsWith(countryCode) 
          ? mobileNumber.slice(countryCode.length) 
          : mobileNumber;
      
      const notificationData = {
        templateName: 'cab_driver_assigned',
        customerCountryCode: countryCode,
        customerNumber: mobileNumber,
        body: {
          name: customerName || 'Customer',
          drivername: assignedDriver.name || 'Driver',
          drivercontact: assignedDriver.mobileNumber || '',
          tripcode: tripCode || '',
          pickuptime: pickupTime ? new Date(pickupTime).toLocaleString() : 'Not specified',
          cartype: carType || 'Not specified',
          carnumber: carNumber || 'Not specified'
        }
      };
      
      await api.sendWhatsAppNotification(notificationData);
      
      setSnackbarMessage('Driver information sent via WhatsApp successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenWhatsAppDialog(false);
    } catch (error: any) {
      console.error('Error sending WhatsApp driver notification:', error);
      setSnackbarMessage(error.message || 'Failed to send WhatsApp driver notification');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSendingNotification(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  return (
    <DetailSection ref={driverSectionRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
          Driver Assignment
        </Typography>
        
        {assignedDriver && currentDriverId && (
          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            onClick={handleOpenWhatsAppDialog}
            disabled={  disabled || !customerMobile || !assignedDriver}
            sx={{
              color: '#25D366',
              borderColor: '#25D366',
              '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.1)' },
              '&.Mui-disabled': { opacity: 0.5 }
            }}
          >
            Send Driver Assigned
          </Button>
        )}
      </Box>
      
      
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: 2, 
          alignItems: 'flex-end',
          bgcolor: 'transparent',
          '& .MuiInputLabel-root': {
            color: 'grey.400'
          },
          '& .MuiOutlinedInput-root': {
            color: 'white',
            '& fieldset': {
              borderColor: 'grey.700'
            },
            '&:hover fieldset': {
              borderColor: 'grey.500'
            }
          },
          '& .MuiSelect-icon': {
            color: 'grey.400'
          }
        }}>
          <FormControl fullWidth sx={{ flex: 0.7 }}>
            <InputLabel id="driver-select-label">Select Driver</InputLabel>
            <Select
              labelId="driver-select-label"
              id="driver-select"
              value={selectedDriverId || ''}
              label="Select Driver"
              onChange={handleDriverChange}
              disabled={  disabled || isLoading || loading}
            >
              {drivers.map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.name} {driver.mobileNumber ? `(${driver.mobileNumber})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            onClick={handleAssignDriver}
            disabled={isLoading || loading || !selectedDriverId || disabled}
            sx={{ minWidth: '120px', height: '50px' }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Assign Driver'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemoveDriver}
            disabled={isLoading || loading || !currentDriverId || disabled || isInProgress}
            sx={{ minWidth: '120px', height: '50px' }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Remove Driver'}
          </Button>
          
          {/* <Button
            variant="outlined"
            onClick={fetchDrivers}
            disabled={ disabled || isLoading || loading}
            sx={{ height: '50px' }}
          >
            Refresh List
          </Button> */}
        </Box>
        
        {/* Always show driver status message if there's a current driver assigned */}
        {(selectedDriverId || currentDriverId) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="grey.400">
              {disabled ? 
                'Cannot modify driver assignment - trip is cancelled' :
                currentDriverId ? 
                'This driver is currently assigned to this trip' : 
                'Click "Assign Driver" to assign this driver to the trip'}
            </Typography>
            
            {/* Display driver details - show for both selected and current driver */}
            {(() => {
              // First try to find the selected driver in the drivers list
              const driverId = selectedDriverId || currentDriverId;
              const selectedDriver = drivers.find(driver => driver.id === driverId);
              
              // If we found the driver or have an assigned driver, show details
              if (selectedDriver || assignedDriver) {
                const driverToShow = selectedDriver || assignedDriver;
                if (!driverToShow) return null;
                
                return (
                  <Box sx={{ 
                    mt: 1, 
                    p: 2, 
                    bgcolor: '#2a2a2a', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiTypography-root': {
                      color: 'grey.300'
                    },
                    '& strong': {
                      color: 'white',
                      marginRight: 1
                    }
                  }}>
                    <Typography variant="body2" fontWeight="medium">
                      {currentDriverId ? 'Assigned Driver Details:' : 'Selected Driver Details:'}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <strong>Name:</strong> {driverToShow.name}
                      </Typography>
                      {driverToShow.mobileNumber && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Mobile:</strong> {driverToShow.mobileNumber}
                        </Typography>
                      )}
                      {driverToShow.licenseNumber && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>License:</strong> {driverToShow.licenseNumber}
                        </Typography>
                      )}
                      {driverToShow.rating !== undefined && driverToShow.rating > 0 && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Rating:</strong> {driverToShow.rating}/5
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }
              return null;
            })()}
          </Box>
        )}
      </Paper>
      
      {/* WhatsApp Driver Assigned Notification Dialog */}
      <Dialog open={openWhatsAppDialog} onClose={handleCloseWhatsAppDialog}>
        <DialogTitle sx={{ bgcolor: '#075E54', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon /> Send Driver Assigned Notification
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: '#1e293b', color: 'white' }}>
          <DialogContentText sx={{ color: '#e2e8f0' }}>
            Send a WhatsApp notification to inform the customer about the assigned driver.
          </DialogContentText>
          
          {assignedDriver && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#2d3748', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Driver Details:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Customer:</Typography>
                <Typography variant="body2">{customerName || 'N/A'}</Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Mobile:</Typography>
                <Typography variant="body2">{customerMobile || 'N/A'}</Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Driver Name:</Typography>
                <Typography variant="body2">{assignedDriver.name || 'N/A'}</Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Pickup Time:</Typography>
                <Typography variant="body2">{pickupTime ? new Date(pickupTime).toLocaleString() : 'N/A'}</Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Driver Mobile:</Typography>
                <Typography variant="body2">{assignedDriver.mobileNumber || 'N/A'}</Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Car Type:</Typography>
                <Typography variant="body2">{carType || 'N/A'}</Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Car Number:</Typography>
                <Typography variant="body2">{carNumber || 'N/A'}</Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Trip ID:</Typography>
                <Typography variant="body2">{tripCode || 'N/A'}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWhatsAppDialog} sx={{ color: 'white' }}>Cancel</Button>
          <Button 
            onClick={handleSendWhatsAppNotification} 
            disabled={sendingNotification || !customerMobile || !assignedDriver || disabled}
            startIcon={<WhatsAppIcon />}
            sx={{ 
              bgcolor: '#25D366', 
              color: 'white',
              '&:hover': { bgcolor: '#128C7E' },
              '&.Mui-disabled': { bgcolor: '#cccccc' }
            }}
          >
            {sendingNotification ? <CircularProgress size={24} /> : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert 
          elevation={6} 
          variant="filled" 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </DetailSection>
  );
};

export default TripDriverSection;
